import React, { useState } from 'react';
import styled from 'styled-components';

const TABS = [
  'About This System',
  'Things to Try',
  'Desktop Icons',
  'Easter Eggs',
  'Keyboard Shortcuts',
  'About DSC',
  'Privacy & Legal',
];

const TAB_CONTENT = {
  'About This System': (
    <>
      <h2>Welcome to Dead Simple Computing's XP Desktop</h2>
      <p>This is an interactive Windows XP desktop experience built to showcase what happens when you DON'T have proper IT support.</p>
      <p>It's a fully interactive recreation complete with working applications, authentic XP-era virus popups, and a healthy dose of early-2000s nostalgia. It's a marketing tool for <strong>Dead Simple Computing</strong> — Reading's friendliest IT support company.</p>
      <h3>How It Works</h3>
      <ul>
        <li><strong>Double-click</strong> any desktop icon to open an application</li>
        <li>Use the <strong>Start Menu</strong> (bottom-left) to access more programs</li>
        <li><strong>Right-click</strong> the desktop for the context menu</li>
        <li>Close windows with the <strong>X button</strong> in the title bar</li>
        <li>Drag windows by their <strong>title bars</strong> to move them</li>
        <li>Click the <strong>taskbar</strong> to switch between open windows</li>
      </ul>
    </>
  ),
  'Things to Try': (
    <>
      <h2>Things to Try</h2>
      <h3>Applications</h3>
      <ul>
        <li><strong>Dead Simple Computing</strong> — Opens our main website in an IE window</li>
        <li><strong>Dead Simple Accounting</strong> — Our sister company's website</li>
        <li><strong>Security Center</strong> — See the state of a PC with zero IT support (spoiler: everything is OFF)</li>
        <li><strong>Command Prompt</strong> — Experience a "ransomware attack" (don't worry, it's fake)</li>
        <li><strong>Windows Update</strong> — 847 critical updates pending since 2004</li>
        <li><strong>Outlook Express</strong> — Compose emails like it's 2003</li>
        <li><strong>Task Manager</strong> — Open from Start Menu to see all the malware running on your "PC". crypto_miner.exe is using 99% CPU...</li>
      </ul>
      <h3>Documents</h3>
      <ul>
        <li><strong>passwords.txt</strong> — The world's worst password management system</li>
        <li><strong>My Finances.xls</strong> — A very questionable spreadsheet</li>
        <li><strong>Security Checklist.rtf</strong> — A regulated sector client's compliance nightmare</li>
        <li><strong>TAX DEADLINES.txt</strong> — Panicked sticky notes about HMRC</li>
        <li><strong>Compliance Calendar</strong> — An overdue compliance calendar</li>
        <li><strong>Cyber Awareness 2004.ppt</strong> — Gary's legendary security training PowerPoint</li>
        <li><strong>Client Reviews</strong> — Read what our clients say about us (Start Menu → Client Reviews)</li>
      </ul>
      <h3>Games & Fun</h3>
      <ul>
        <li><strong>Minesweeper</strong> — Classic XP Minesweeper, fully playable</li>
        <li><strong>3D Pinball</strong> — The REAL Space Cadet Pinball (Start → All Programs → Games)</li>
        <li><strong>Calculator</strong> — Standard XP calculator</li>
        <li><strong>Winamp</strong> — It really whips the llama's...</li>
        <li><strong>My Pictures</strong> — A surprise awaits</li>
        <li><strong>Paint</strong> — Draw something via the Start Menu</li>
      </ul>
    </>
  ),
  'Desktop Icons': (
    <>
      <h2>Desktop Icons</h2>
      <p>Your desktop contains the following shortcuts:</p>
      <ul>
        <li><strong>Dead Simple Computing</strong> — Our main website (Internet Explorer)</li>
        <li><strong>Dead Simple Accounting</strong> — Our accounting sister site</li>
        <li><strong>Web Design & Hosting</strong> — Dead Simple Web Solutions page</li>
        <li><strong>Outlook Express</strong> — Email client</li>
        <li><strong>Security Checklist.rtf</strong> — Compliance document</li>
        <li><strong>passwords.txt</strong> — (Please don't actually do this)</li>
        <li><strong>My Finances.xls</strong> — Spreadsheet</li>
        <li><strong>TAX DEADLINES.txt</strong> — Sticky notes</li>
        <li><strong>Cyber Awareness 2004.ppt</strong> — Gary's mandatory security training</li>
        <li><strong>My Computer</strong> — Browse the file system</li>
        <li><strong>Compliance Calendar</strong> — Calendar view</li>
        <li><strong>Winamp</strong> — Music player</li>
        <li><strong>Recycle Bin</strong> — Your deleted files</li>
      </ul>
      <p>More apps available via the <strong>Start Menu</strong> (Security Center, Command Prompt, Windows Update, Calculator, Pinball, etc.)</p>
    </>
  ),
  'Easter Eggs': (
    <>
      <h2>Easter Eggs & Hidden Features</h2>
      <p>There are several hidden features throughout the desktop:</p>
      <ul>
        <li><strong>Blue Screen of Death</strong> — Press <kbd>Ctrl+Shift+B</kbd> to trigger a classic BSoD. Click anywhere to dismiss.</li>
        <li><strong>Clippy</strong> — Wait around for about 60 seconds and our helpful (and very sarcastic) paperclip assistant will appear with IT advice.</li>
        <li><strong>Matrix Screensaver</strong> — Right-click the desktop → "Screen Saver Preview" to activate the Matrix rain effect. Move the mouse to dismiss.</li>
        <li><strong>Virus Popups</strong> — Those annoying popups are intentional! They recreate the authentic 2005 experience. Click the 🛡️ button in the bottom-right corner (above the taskbar) to stop them permanently.</li>
        <li><strong>Right-Click Menu</strong> — Right-click the desktop background for the classic XP context menu. Try "Properties" to open Display Properties.</li>
        <li><strong>Taskbar Right-Click</strong> — Right-click the taskbar to open Task Manager.</li>
        <li><strong>Task Manager</strong> — Open it to discover crypto_miner.exe, bonzibuddy.exe, and other "legitimate" software running on your PC.</li>
        <li><strong>Shutdown</strong> — Click Start → Turn Off Computer for the authentic XP shutdown experience.</li>
      </ul>
    </>
  ),
  'Keyboard Shortcuts': (
    <>
      <h2>Keyboard Shortcuts</h2>
      <ul>
        <li><kbd>Ctrl+Shift+B</kbd> — Blue Screen of Death</li>
      </ul>
      <h3>Window Controls</h3>
      <ul>
        <li>Click the <strong>X</strong> button to close a window</li>
        <li>Click the <strong>□</strong> button to maximize/restore</li>
        <li>Click the <strong>_</strong> button to minimize</li>
        <li>Click a window's <strong>taskbar button</strong> to toggle minimize</li>
      </ul>
    </>
  ),
  'About DSC': (
    <>
      <h2>About Dead Simple Computing</h2>
      <p>Dead Simple Computing provides <strong>Managed IT</strong>, <strong>Cyber Security</strong>, and <strong>Microsoft 365</strong> services to businesses in Reading and the Thames Valley.</p>
      <p>If your actual PC looks anything like this one, please call us immediately.</p>
      <h3>Our Services</h3>
      <ul>
        <li><strong>Managed IT Support</strong> — Proactive monitoring, helpdesk, and on-site support</li>
        <li><strong>Cyber Security</strong> — Cyber Essentials, penetration testing, security audits</li>
        <li><strong>Microsoft 365</strong> — Migration, management, and optimisation</li>
        <li><strong>Cloud Infrastructure</strong> — Azure and hybrid cloud solutions</li>
        <li><strong>Compliance</strong> — GDPR, ISO 27001, Cyber Essentials certification</li>
        <li><strong>Backup & Disaster Recovery</strong> — Because stuff happens</li>
      </ul>
      <h3>Contact Us</h3>
      <p style={{ fontSize: '14px' }}>
        <span role="img" aria-label="phone">📞</span> <strong style={{ color: '#0066cc' }}>0118 359 2220</strong>
      </p>
      <p style={{ fontSize: '14px' }}>
        <span role="img" aria-label="web">🌐</span> <strong style={{ color: '#0066cc' }}>deadsimplecomputing.co.uk</strong>
      </p>
      <p style={{ fontSize: '14px' }}>
        <span role="img" aria-label="web">🌐</span> <strong style={{ color: '#0066cc' }}>deadsimpleaccounting.co.uk</strong>
      </p>
    </>
  ),
  'Privacy & Legal': (
    <>
      <h2>Privacy Policy & Terms of Use</h2>
      <p>Dead Simple Computing Ltd takes your privacy seriously — which is why this website <strong>doesn't collect any data at all</strong>.</p>
      <h3>Key Points</h3>
      <ul>
        <li><strong>No cookies</strong> — We don't track you</li>
        <li><strong>No analytics</strong> — We have no idea how many people visit</li>
        <li><strong>No forms</strong> — We can't collect your data even if we wanted to</li>
        <li><strong>No user accounts</strong> — Everything runs locally in your browser</li>
        <li><strong>The virus alerts are fake</strong> — Your computer is fine (probably)</li>
      </ul>
      <h3>Disclaimer</h3>
      <p>This website is a parody/tribute to Windows XP and is not affiliated with Microsoft. The simulated virus alerts, security warnings, and other notifications are fictional and included for comedic effect.</p>
      <p>We accept no responsibility for nostalgia-induced emotional responses or time lost playing Minesweeper.</p>
      <h3>Full Policy</h3>
      <p>For the complete Privacy Policy & Terms of Use, open it from the <strong>Start Menu → Help and Support</strong> or search for "Privacy Policy" in the Start Menu.</p>
      <h3>Contact</h3>
      <p>📞 <strong>0118 359 2220</strong></p>
      <p>🌐 <strong>deadsimplecomputing.co.uk</strong></p>
    </>
  ),
};

function HelpAndSupport() {
  const [activeTab, setActiveTab] = useState('About This System');

  return (
    <Container>
      <div className="header">
        <div className="header__title">Help and Support Center</div>
        <div className="header__subtitle">Windows XP Professional</div>
      </div>
      <div className="body">
        <div className="sidebar">
          <div className="sidebar__title">Help Topics</div>
          {TABS.map(tab => (
            <div
              key={tab}
              className={`sidebar__item ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </div>
          ))}
        </div>
        <div className="content">
          {TAB_CONTENT[activeTab]}
        </div>
      </div>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
  font-family: Tahoma, sans-serif;
  font-size: 12px;

  .header {
    background: linear-gradient(to right, #2955a5, #5b8dde);
    padding: 12px 16px;
    color: #fff;
  }
  .header__title {
    font-size: 18px;
    font-weight: bold;
  }
  .header__subtitle {
    font-size: 11px;
    opacity: 0.8;
    margin-top: 2px;
  }

  .body {
    display: flex;
    flex: 1;
    overflow: hidden;
  }

  .sidebar {
    width: 180px;
    background: #ece9d8;
    border-right: 1px solid #aca899;
    padding: 8px 0;
    flex-shrink: 0;
    overflow-y: auto;
  }
  .sidebar__title {
    font-weight: bold;
    padding: 4px 12px;
    color: #003399;
    font-size: 12px;
    margin-bottom: 4px;
  }
  .sidebar__item {
    padding: 4px 12px 4px 20px;
    cursor: pointer;
    color: #333;
    &:hover {
      background: #316ac5;
      color: #fff;
    }
    &.active {
      background: #316ac5;
      color: #fff;
      font-weight: bold;
    }
  }

  .content {
    flex: 1;
    padding: 16px 24px;
    overflow-y: auto;
    line-height: 1.6;
    color: #333;

    h2 {
      color: #003399;
      font-size: 16px;
      margin: 0 0 12px 0;
      border-bottom: 1px solid #ece9d8;
      padding-bottom: 8px;
    }
    h3 {
      color: #003399;
      font-size: 13px;
      margin: 16px 0 6px 0;
    }
    p {
      margin: 4px 0;
    }
    ul {
      margin: 4px 0;
      padding-left: 20px;
    }
    li {
      margin: 3px 0;
    }
    strong {
      color: #222;
    }
    kbd {
      background: #ece9d8;
      border: 1px solid #aca899;
      border-radius: 3px;
      padding: 1px 5px;
      font-family: Tahoma, sans-serif;
      font-size: 11px;
    }
  }
`;

export default HelpAndSupport;
